/**
 * MCP (Model Context Protocol) OAuth scopes and FGA identifiers for delegated
 * agent access. Configure matching scopes in WorkOS Connect and FGA resource
 * types in the WorkOS Dashboard before enforcing in production.
 */
import type { AccountType, NaturalBalance, RevenueClass } from '../generated/enums.js';
import type { PermissionKey } from '../permissions.js';

export interface McpCoaScopeRequest {
  entityId: string;
  bookId?: string;
}

export interface McpCoaRenameItem {
  code: string;
  name: string;
}

export interface McpCoaRenameRequest extends McpCoaScopeRequest {
  renames: McpCoaRenameItem[];
}

export interface McpCoaRenameApplyRequest extends McpCoaRenameRequest {
  confirmationId: string;
}

export interface McpCoaCreateItem {
  code: string;
  name: string;
  parentCode?: string | null;
  accountType: AccountType;
  naturalBalance: NaturalBalance;
  isPostable: boolean;
  revenueClass?: RevenueClass | null;
}

export interface McpCoaCreateRequest {
  /** Required for the active assigned chart; optional when versionId targets a DRAFT. */
  entityId?: string;
  /** Optional assertion for the active assigned chart. */
  bookId?: string;
  /** Direct target is accepted only for a DRAFT chart version. */
  versionId?: string;
  accounts: McpCoaCreateItem[];
}

export interface McpCoaCreateApplyRequest extends McpCoaCreateRequest {
  confirmationId: string;
}

export interface McpCoaAffectedAssignment {
  entityId: string;
  entityName: string;
  bookId: string;
}

export interface McpCoaRenamePreviewChange {
  kind: 'rename';
  code: string;
  glAccountId: string;
  before: string;
  after: string;
}

export type McpCoaCreatePreviewChange =
  | (McpCoaCreateItem & { kind: 'create'; effectiveFrom: string })
  | (McpCoaCreateItem & { kind: 'unchanged'; glAccountId: string; effectiveFrom: string });

export interface McpCoaRenameResult {
  versionId: string;
  affectedAssignments: McpCoaAffectedAssignment[];
  changed: McpCoaRenamePreviewChange[];
  unchanged: McpCoaRenamePreviewChange[];
  auditLogId: string;
}

export interface McpCoaCreateResult {
  versionId: string;
  affectedAssignments: McpCoaAffectedAssignment[];
  created: { code: string; name: string; glAccountId: string }[];
  unchanged: { code: string; name: string; glAccountId: string }[];
  auditLogId: string;
}

export interface McpCoaMaintenancePreviewResponse {
  versionId: string;
  versionName: string;
  versionNumber: string;
  bookId?: string;
  affectedAssignments: McpCoaAffectedAssignment[];
  resourceVersion: string;
  changes: (McpCoaRenamePreviewChange | McpCoaCreatePreviewChange)[];
}

export interface McpCoaPostabilityRequest extends McpCoaScopeRequest {
  accounts: {
    code: string;
    isPostable: boolean;
    /** May accompany reactivation when Revenue Tax requires a class. */
    revenueClass?: RevenueClass | null;
  }[];
}

export interface McpCoaPostabilityChange {
  code: string;
  name: string;
  glAccountId: string;
  before: { isPostable: boolean; revenueClass: RevenueClass | null };
  after: { isPostable: boolean; revenueClass: RevenueClass | null };
}

export interface McpCoaPostabilityPreview extends Omit<
  McpCoaMaintenancePreviewResponse,
  'changes'
> {
  changes: McpCoaPostabilityChange[];
}

export interface McpCoaPostabilityResult {
  versionId: string;
  affectedAssignments: McpCoaAffectedAssignment[];
  changed: McpCoaPostabilityChange[];
  unchanged: McpCoaPostabilityChange[];
  auditLogId: string;
}

export type McpCoaAccountStatus = 'ACTIVE' | 'INACTIVE';

export interface McpCoaStatusRequest extends McpCoaScopeRequest {
  accounts: { code: string; status: McpCoaAccountStatus }[];
}

export interface McpCoaStatusChange {
  code: string;
  name: string;
  glAccountId: string;
  before: McpCoaAccountStatus;
  after: McpCoaAccountStatus;
}

export interface McpCoaStatusPreview extends Omit<McpCoaMaintenancePreviewResponse, 'changes'> {
  changes: McpCoaStatusChange[];
}

export interface McpCoaStatusResult {
  versionId: string;
  affectedAssignments: McpCoaAffectedAssignment[];
  changed: McpCoaStatusChange[];
  unchanged: McpCoaStatusChange[];
  auditLogId: string;
}

/** A hard delete is limited to one account in an explicit DRAFT chart version. */
export interface McpCoaDraftDeleteRequest {
  versionId: string;
  code: string;
}

export interface McpCoaDraftDeleteAccount {
  glAccountId: string;
  code: string;
  name: string;
  parentCode: string | null;
}

export interface McpCoaDraftDeletePreview {
  versionId: string;
  versionName: string;
  versionNumber: string;
  resourceVersion: string;
  affectedAssignments: McpCoaAffectedAssignment[];
  account: McpCoaDraftDeleteAccount;
}

export interface McpCoaDraftDeleteResult {
  versionId: string;
  deleted: McpCoaDraftDeleteAccount;
  auditLogId: string;
}

/** OAuth 2.1 scopes issued to MCP clients after user consent (WorkOS Connect). */
export const McpApBillOAuthScope = {
  READ: 'mcp:apbills:read',
} as const;
export type McpApBillOAuthScope = (typeof McpApBillOAuthScope)[keyof typeof McpApBillOAuthScope];

export const MCP_AP_BILL_OAUTH_SCOPES = [
  McpApBillOAuthScope.READ,
] as const satisfies readonly McpApBillOAuthScope[];

/**
 * FGA resource type slugs registered in WorkOS. Structural hierarchy:
 * `apbills` (tenant) → `bill` (per-bill, Phase 3+).
 */
export const McpFgaResourceType = {
  APBILLS: 'apbills',
  BILL: 'bill',
} as const;
export type McpFgaResourceType = (typeof McpFgaResourceType)[keyof typeof McpFgaResourceType];

/**
 * FGA permission slugs on MCP resources. Maps to RBAC `ap:bill:read` for the
 * read-only MVP; write/action permissions arrive in Phase 3+.
 */
export const McpFgaPermission = {
  APBILLS_READ: 'apbills:read',
  APBILLS_APPROVE: 'apbills:approve',
  APBILLS_PAY: 'apbills:pay',
} as const;
export type McpFgaPermission = (typeof McpFgaPermission)[keyof typeof McpFgaPermission];

/** RBAC permission required for MCP AP-bill read tools (acting user baseline). */
export const MCP_AP_BILL_RBAC_READ = 'ap:bill:read' as const;

/** RBAC permission required for MCP AP payment rows — mirrors the API route. */
export const MCP_AP_PAYMENT_RBAC_READ = 'ap:payment:read' as const;

/** RBAC permission required for MCP received-NF read tools — mirrors the API route. */
export const MCP_AP_RECEIVED_NF_RBAC_READ = 'ap:received-nf:read' as const;

// ============================================================================
// Generalized MCP authorization registry (single source of truth)
// ----------------------------------------------------------------------------
// Each MCP tool authorizes on the INTERSECTION of three axes:
//   1. OAuth delegation scope  — what the user *consented* to let the agent do
//   2. RBAC permission         — what the user is *allowed* to do (the ceiling)
//   3. FGA resource check       — fine-grained, resource-scoped (optional)
//
// `rbacPermission` is typed as `PermissionKey`, so the mapping CANNOT reference
// a non-existent permission — RBAC and MCP scopes stay in sync at compile time,
// and a CI drift-guard test additionally proves tool/scope coverage at runtime.
// There is NO parallel `mcp:` RBAC permission tree; `mcp:*` are *scopes* only.
// ============================================================================

/**
 * Generalized MCP OAuth delegation scopes — one per tool domain. A scope is a
 * consent grant the user gives a specific agent (lives in WorkOS Connect), and
 * maps to an existing RBAC {@link PermissionKey} via {@link MCP_TOOL_AUTH}.
 */
export const McpOAuthScope = {
  AP_BILLS_READ: 'mcp:apbills:read',
  AP_BILLS_MANAGE: 'mcp:apbills:manage',
  AP_VENDORS_READ: 'mcp:apvendors:read',
  AP_VENDORS_MANAGE: 'mcp:apvendors:manage',
  AP_VENDOR_KNOWLEDGE_MANAGE: 'mcp:apvendorknowledge:manage',
  EXPENSE_ACCRUALS_READ: 'mcp:apexpenseaccruals:read',
  EXPENSE_ACCRUALS_MANAGE: 'mcp:apexpenseaccruals:manage',
  AP_RECEIVED_NFS_READ: 'mcp:apreceivednfs:read',
  GL_CLOSING_READ: 'mcp:glclosing:read',
  ORG_READ: 'mcp:org:read',
  GL_READ: 'mcp:gl:read',
  GL_MANAGE: 'mcp:gl:manage',
  SETTINGS_READ: 'mcp:settings:read',
  SETTINGS_MANAGE: 'mcp:settings:manage',
  CASHFLOW_MANAGE: 'mcp:cashflow:manage',
  BUDGET_READ: 'mcp:budget:read',
  BUDGET_MANAGE: 'mcp:budget:manage',
  IDENTITY_READ: 'mcp:identity:read',
  AR_READ: 'mcp:ar:read',
  CASHFLOW_READ: 'mcp:cashflow:read',
  // FP&A DRE authoring surface (slice F / ALC-1910) — fpa_* tools.
  FPA_READ: 'mcp:fpa:read',
  FPA_MANAGE: 'mcp:fpa:manage',
  HCM_MANAGE: 'mcp:hcm:manage',
  BILLING_READ: 'mcp:billing:read',
  BILLING_MANAGE: 'mcp:billing:manage',
  WORKFLOW_READ: 'mcp:workflow:read',
  WORKFLOW_MANAGE: 'mcp:workflow:manage',
} as const;
export type McpOAuthScope = (typeof McpOAuthScope)[keyof typeof McpOAuthScope];

/** Canonical MCP tool names (kept in sync with `apps/mcp` tool definitions). */
export const McpToolName = {
  AP_BILLS_LIFECYCLE_GUIDE: 'ap_bills_lifecycle_guide',
  AP_BILLS_UPDATE: 'ap_bills_update',
  AP_BILLS_LIST: 'ap_bills_list',
  AP_BILLS_GET: 'ap_bills_get',
  AP_BILLS_SUMMARY: 'ap_bills_summary',
  AP_BILLS_PERIOD_STATS: 'ap_bills_period_stats',
  AP_BILLS_TIMELINE: 'ap_bills_timeline',
  AP_BILLS_PAYMENTS_LIST: 'ap_bills_payments_list',
  AP_BILLS_DOCUMENT_UPLOAD_OPEN: 'ap_bills_document_upload_open',
  AP_BILLS_PAYMENTS_UPDATE: 'ap_bills_payments_update',
  AP_BILLS_PAYMENTS_CREATE: 'ap_bills_payments_create',
  AP_VENDORS_CREATE: 'ap_vendors_create',
  AP_RECURRING_BILLS_CREATE: 'ap_recurring_bills_create',
  AP_VENDORS_FIND: 'ap_vendors_find',
  AP_RECURRING_BILLS_LIST: 'ap_recurring_bills_list',
  AP_RECURRING_BILLS_UPDATE: 'ap_recurring_bills_update',
  AP_VENDORS_PAYMENT_METHODS_LIST: 'ap_vendors_payment_methods_list',
  AP_VENDORS_PAYMENT_METHODS_CREATE: 'ap_vendors_payment_methods_create',
  AP_EXPENSE_ACCRUALS_LIST: 'ap_expense_accruals_list',
  AP_EXPENSE_ACCRUALS_CONFIRM: 'ap_expense_accruals_confirm',
  AP_VENDOR_KNOWLEDGE_LIST: 'ap_vendor_knowledge_list',
  AP_VENDOR_KNOWLEDGE_RESOLVE: 'ap_vendor_knowledge_resolve',
  AP_VENDOR_KNOWLEDGE_CREATE: 'ap_vendor_knowledge_create',
  AP_VENDOR_KNOWLEDGE_NEW_VERSION: 'ap_vendor_knowledge_new_version',
  AP_VENDOR_KNOWLEDGE_DEACTIVATE: 'ap_vendor_knowledge_deactivate',
  AP_RECEIVED_NFS_LIST: 'ap_received_nfs_list',
  AP_RECEIVED_NFS_GET: 'ap_received_nfs_get',
  GL_CLOSING_CUSTOM_TASKS_LIST: 'gl_closing_custom_tasks_list',
  GL_MANUAL_ENTRY_DRAFT_CREATE: 'gl_manual_entry_draft_create',
  GL_MANUAL_ENTRY_DRAFT_POST: 'gl_manual_entry_draft_post',
  ORG_ENTITIES_LIST: 'org_entities_list',
  ORG_COST_CENTERS_LIST: 'org_cost_centers_list',
  ORG_DEPARTMENTS_LIST: 'org_departments_list',
  GL_BOOKS_LIST: 'gl_books_list',
  GL_PERIODS_LIST: 'gl_periods_list',
  GL_TRIAL_BALANCE_JSONL_GET: 'gl_trial_balance_jsonl_get',
  GL_JOURNAL_LINES_JSONL_GET: 'gl_journal_lines_jsonl_get',
  GL_LEDGER_VIEW_PRESETS_LIST: 'gl_ledger_view_presets_list',
  GL_LEDGER_VIEW_PRESETS_CREATE: 'gl_ledger_view_presets_create',
  GL_LEDGER_VIEW_PRESETS_UPDATE: 'gl_ledger_view_presets_update',
  GL_LEDGER_VIEW_PRESETS_DELETE: 'gl_ledger_view_presets_delete',
  GL_LEDGER_VIEW_PRESETS_SET_DEFAULT: 'gl_ledger_view_presets_set_default',
  BUDGET_SETTINGS_GET: 'budget_settings_get',
  BUDGET_LIST: 'budget_list',
  BUDGET_GET: 'budget_get',
  BUDGET_LINES_LIST: 'budget_lines_list',
  BUDGET_LINE_GET: 'budget_line_get',
  BUDGET_RESULTS: 'budget_results',
  BUDGET_LINE_UTILIZATION: 'budget_line_utilization',
  BUDGET_HISTORY: 'budget_history',
  BUDGET_EXCEPTIONS_LIST: 'budget_exceptions_list',
  BUDGET_CHECK_BILL: 'budget_check_bill',
  BUDGET_CREATE: 'budget_create',
  BUDGET_CREATE_FROM_TEMPLATE: 'budget_create_from_template',
  BUDGET_UPDATE: 'budget_update',
  BUDGET_DUPLICATE: 'budget_duplicate',
  BUDGET_CREATE_REVISION: 'budget_create_revision',
  BUDGET_VERSIONS_LIST: 'budget_versions_list',
  BUDGET_LINE_CREATE: 'budget_line_create',
  BUDGET_LINE_UPDATE: 'budget_line_update',
  BUDGET_LINES_BULK_UPSERT: 'budget_lines_bulk_upsert',
  BUDGET_LINES_BULK_ARCHIVE: 'budget_lines_bulk_archive',
  BUDGET_ACTIVATE: 'budget_activate',
  BUDGET_DEACTIVATE: 'budget_deactivate',
  BUDGET_ARCHIVE: 'budget_archive',
  BUDGET_REFORECAST_PROMOTION_PREVIEW: 'budget_reforecast_promotion_preview',
  BUDGET_REFORECAST_PROMOTE: 'budget_reforecast_promote',
  BUDGET_RESYNC: 'budget_resync',
  BUDGET_LINE_DERIVATION_RULE_GET: 'budget_line_derivation_rule_get',
  BUDGET_LINE_DERIVATION_RULE_PUT: 'budget_line_derivation_rule_put',
  BUDGET_PAYROLL_SCENARIO_SET: 'budget_payroll_scenario_set',
  BUDGET_LINE_DERIVED_LIMITS_REVIEW: 'budget_line_derived_limits_review',
  BUDGET_LINE_DERIVED_LIMITS_APPROVE: 'budget_line_derived_limits_approve',
  AP_MANAGERIAL_ACCOUNTS_LIST: 'ap_managerial_accounts_list',
  GL_ACCOUNTS_LIST: 'gl_accounts_list',
  GL_ACCOUNTS_RENAME: 'gl_accounts_rename',
  GL_ACCOUNTS_CREATE: 'gl_accounts_create',
  GL_ACCOUNTS_SET_POSTABLE: 'gl_accounts_set_postable',
  GL_ACCOUNTS_SET_STATUS: 'gl_accounts_set_status',
  GL_ACCOUNTS_DELETE: 'gl_accounts_delete',
  ASSETS_MIGRATION_READINESS_GET: 'assets_migration_readiness_get',
  ASSETS_CLASS_ACCOUNTS_SET: 'assets_class_accounts_set',
  ASSETS_MIGRATION_IMPORT: 'assets_migration_import',
  ASSETS_REGISTER: 'assets_register',
  ORG_SEGMENTS_LIST: 'org_segments_list',
  ORG_SEGMENT_VALUES_LIST: 'org_segment_values_list',
  IDENTITY_USERS_FIND: 'identity_users_find',
  AR_INVOICES_LIST: 'ar_invoices_list',
  AR_INVOICES_GET: 'ar_invoices_get',
  AR_INVOICES_SUMMARY: 'ar_invoices_summary',
  AR_INVOICES_PERIOD_STATS: 'ar_invoices_period_stats',
  AR_RECEIPTS_LIST: 'ar_receipts_list',
  AR_CUSTOMERS_FIND: 'ar_customers_find',
  AR_CUSTOMER_CREATE: 'ar_customer_create',
  AR_CUSTOMER_CREDIT_RELEASES_GET: 'ar_customer_credit_releases_get',
  AR_CUSTOMER_CREDIT_RELEASE: 'ar_customer_credit_release',
  AR_CUSTOMER_CREDIT_RELEASE_REVERSE: 'ar_customer_credit_release_reverse',
  BILLING_SERVICES_LIST: 'billing_services_list',
  BILLING_SERVICES_GET: 'billing_services_get',
  BILLING_SERVICE_CREATE: 'billing_service_create',
  BILLING_SERVICE_UPDATE: 'billing_service_update',
  BILLING_SERVICE_ARCHIVE: 'billing_service_archive',
  BILLING_CONTRACTS_LIST: 'billing_contracts_list',
  BILLING_CONTRACTS_GET: 'billing_contracts_get',
  BILLING_CONTRACT_CREATE: 'billing_contract_create',
  BILLING_CONTRACT_ACTIVATE: 'billing_contract_activate',
  BILLING_INVOICE_GENERATE: 'billing_invoice_generate',
  BILLING_AR_FORECAST_GET: 'billing_ar_forecast_get',
  BILLING_SAAS_METRICS_GET: 'billing_saas_metrics_get',
  BILLING_MRR_MOVEMENTS_GET: 'billing_mrr_movements_get',
  // ALC-4474 — Cost Services (provisioned third-party costs attached to billing services).
  COST_SERVICES_CREATE_FROM_TEMPLATE: 'cost_services_create_from_template',
  COST_SERVICES_GET: 'cost_services_get',
  COST_SERVICES_LIST: 'cost_services_list',
  COST_SERVICES_PATCH_KNOBS: 'cost_services_patch_knobs',
  COST_SERVICES_ACTIVATE: 'cost_services_activate',
  COST_SERVICES_DEACTIVATE: 'cost_services_deactivate',
  COST_SERVICES_ATTACH_BILLING_SERVICE: 'cost_services_attach_billing_service',
  COST_SERVICES_END_ATTACHMENT: 'cost_services_end_attachment',
  COST_SERVICES_UPDATE_ATTACHMENT: 'cost_services_update_attachment',
  COST_ENTRIES_LIST_OPEN: 'cost_entries_list_open',
  COST_ENTRIES_GET: 'cost_entries_get',
  COST_ENTRIES_HEAL_PENDING: 'cost_entries_heal_pending',
  COST_ENTRIES_AGING: 'cost_entries_aging',
  COST_ENTRIES_CLOSE: 'cost_entries_close',
  COST_ENTRIES_CLOSE_AWAITING: 'cost_entries_close_awaiting',
  COST_MATCHES_SUGGEST: 'cost_matches_suggest',
  COST_MATCHES_CONFIRM: 'cost_matches_confirm',
  COST_MATCHES_CREATE: 'cost_matches_create',
  COST_MATCHES_UNDO: 'cost_matches_undo',
  COST_LINES_SET_DESTINATION: 'cost_lines_set_destination',
  COST_LINES_SET_SERVICE_CHOICE: 'cost_lines_set_service_choice',
  RECONCILIATION_BALANCE_PROOF_GET: 'reconciliation_balance_proof_get',
  // The reconciliation agent's read tools (ALC-4666, ADR 0032: one set of tools, two doors).
  RECONCILIATION_OPEN_BILL_GET: 'reconciliation_open_bill_get',
  RECONCILIATION_VENDOR_OPEN_BILLS_LIST: 'reconciliation_vendor_open_bills_list',
  CASHFLOW_PROJECTION_GET: 'cashflow_projection_get',
  CASHFLOW_SUMMARY_GET: 'cashflow_summary_get',
  CASHFLOW_VARIANCE_ANALYZE: 'cashflow_variance_analyze',
  CASHFLOW_LIQUIDITY_RISKS_LIST: 'cashflow_liquidity_risks_list',
  CASHFLOW_RECEIPTS_CONCENTRATION_ANALYZE: 'cashflow_receipts_concentration_analyze',
  CASHFLOW_FUTURE_OBLIGATIONS_LIST: 'cashflow_future_obligations_list',
  CASHFLOW_ALERT_EXPLAIN: 'cashflow_alert_explain',
  CASHFLOW_CELL_DRILLDOWN: 'cashflow_cell_drilldown',
  CASHFLOW_MANUAL_ITEMS_LIST: 'cashflow_manual_items_list',
  CASHFLOW_LIQUIDITY_CONFIG_GET: 'cashflow_liquidity_config_get',
  CASHFLOW_MANUAL_ITEM_CREATE: 'cashflow_manual_item_create',
  CASHFLOW_MANUAL_ITEM_UPDATE: 'cashflow_manual_item_update',
  CASHFLOW_MANUAL_ITEM_DELETE: 'cashflow_manual_item_delete',
  CASHFLOW_LIQUIDITY_CONFIG_PUT: 'cashflow_liquidity_config_put',
  FPA_TOOLS_GUIDE: 'fpa_tools_guide',
  FPA_PLAYBOOKS_LIST: 'fpa_playbooks_list',
  FPA_PLAYBOOKS_GET: 'fpa_playbooks_get',
  FPA_ROLLUPS_LIST: 'fpa_rollups_list',
  FPA_ROLLUPS_GET: 'fpa_rollups_get',
  FPA_ROLLUPS_CREATE: 'fpa_rollups_create',
  FPA_ROLLUPS_UPDATE: 'fpa_rollups_update',
  FPA_ROLLUPS_ARCHIVE: 'fpa_rollups_archive',
  FPA_ROLLUP_RESTORE_DEFAULT: 'fpa_rollup_restore_default',
  FPA_FORECAST_RULE_SETS_LIST: 'fpa_forecast_rule_sets_list',
  FPA_FORECAST_RULE_SETS_GET: 'fpa_forecast_rule_sets_get',
  FPA_FORECAST_RULE_SETS_CREATE: 'fpa_forecast_rule_sets_create',
  FPA_FORECAST_RULE_SETS_UPDATE: 'fpa_forecast_rule_sets_update',
  FPA_FORECAST_RULE_SETS_ARCHIVE: 'fpa_forecast_rule_sets_archive',
  FPA_TEMPLATES_LIST: 'fpa_templates_list',
  FPA_TEMPLATES_GET: 'fpa_templates_get',
  FPA_TEMPLATES_CREATE: 'fpa_templates_create',
  FPA_TEMPLATES_CREATE_FROM_STANDARD: 'fpa_templates_create_from_standard',
  FPA_TEMPLATES_UPDATE: 'fpa_templates_update',
  FPA_TEMPLATES_ARCHIVE: 'fpa_templates_archive',
  FPA_TEMPLATES_UNARCHIVE: 'fpa_templates_unarchive',
  FPA_TEMPLATE_EVALUATE: 'fpa_template_evaluate',
  FPA_BUDGET_VS_ACTUAL: 'fpa_budget_vs_actual',
  FPA_EXECUTIVE_SNAPSHOT: 'fpa_executive_snapshot',
  FPA_VARIANCE_EXPLAIN: 'fpa_variance_explain',
  FPA_CELL_DRILLDOWN: 'fpa_cell_drilldown',
  FPA_METRICS_LIST: 'fpa_metrics_list',
  FPA_METRIC_SERIES_UPSERT: 'fpa_metric_series_upsert',
  FPA_REVENUE_WATERFALL: 'fpa_revenue_waterfall',
  FPA_METRIC_RESTATEMENT_REQUEST: 'fpa_metric_restatement_request',
  FPA_METRIC_RESTATEMENT_APPROVE: 'fpa_metric_restatement_approve',
  FPA_METRIC_SERIES_DELETE: 'fpa_metric_series_delete',
  FPA_METRIC_POINT_DELETE: 'fpa_metric_point_delete',
  FPA_METRIC_POINT_SOURCES_GET: 'fpa_metric_point_sources_get',
  FPA_ASSUMPTIONS_LIST: 'fpa_assumptions_list',
  FPA_ASSUMPTIONS_CREATE: 'fpa_assumptions_create',
  FPA_ASSUMPTIONS_UPDATE: 'fpa_assumptions_update',
  FPA_ASSUMPTIONS_DELETE: 'fpa_assumptions_delete',
  FPA_LINE_OVERRIDES_LIST: 'fpa_line_overrides_list',
  FPA_LINE_OVERRIDES_CREATE: 'fpa_line_overrides_create',
  FPA_LINE_OVERRIDES_DELETE: 'fpa_line_overrides_delete',
  FPA_SNAPSHOT_FREEZE: 'fpa_snapshot_freeze',
  FPA_SNAPSHOTS_LIST: 'fpa_snapshots_list',
  FPA_SNAPSHOTS_GET: 'fpa_snapshots_get',
  FPA_SNAPSHOTS_DIFF: 'fpa_snapshots_diff',
  FPA_SNAPSHOT_ASOF_GET: 'fpa_snapshot_asof_get',
  FPA_SNAPSHOT_CELL_DRILLDOWN: 'fpa_snapshot_cell_drilldown',
  FPA_FORECAST_ACCURACY: 'fpa_forecast_accuracy',
  HCM_PJ_COLLABORATOR_CREATE: 'hcm_pj_collaborator_create',
  FPA_HEADCOUNT_PLAN_CREATE: 'fpa_headcount_plan_create',
  FPA_HEADCOUNT_PLAN_GET: 'fpa_headcount_plan_get',
  FPA_HEADCOUNT_PLAN_REPORT: 'fpa_headcount_plan_report',
  FPA_HEADCOUNT_HIRE_SUGGESTIONS_LIST: 'fpa_headcount_hire_suggestions_list',
  FPA_HEADCOUNT_POSITION_CREATE: 'fpa_headcount_position_create',
  FPA_HEADCOUNT_POSITION_UPDATE: 'fpa_headcount_position_update',
  FPA_HEADCOUNT_POSITION_CANCEL: 'fpa_headcount_position_cancel',
  FPA_HEADCOUNT_HIRE_CONFIRM: 'fpa_headcount_hire_confirm',
  INTAKE_RULE_LIST: 'intake_rule_list',
  INTAKE_RULE_GET: 'intake_rule_get',
  INTAKE_RULE_REPLAY: 'intake_rule_replay',
  INTAKE_RULE_PROPOSE: 'intake_rule_propose',
  INTAKE_RULE_CREATE: 'intake_rule_create',
  INTAKE_RULE_UPDATE: 'intake_rule_update',
  INTAKE_RULE_DELETE: 'intake_rule_delete',
  REVENUE_TAX_SETUP_GET: 'fiscal_revenue_tax_setup_get',
  REVENUE_TAX_CLASSES_SET: 'fiscal_revenue_tax_classes_set',
  REVENUE_TAX_ACCOUNTS_ENSURE: 'fiscal_revenue_tax_accounts_ensure',
  REVENUE_TAX_CONFIGURE: 'fiscal_revenue_tax_configure',
  REVENUE_TAX_RULE_OVERRIDE_UPSERT: 'fiscal_revenue_tax_rule_override_upsert',
  REVENUE_TAX_RULE_OVERRIDE_END: 'fiscal_revenue_tax_rule_override_end',
  REVENUE_TAX_BACKFILL_PREVIEW: 'fiscal_revenue_tax_backfill_preview',
  REVENUE_TAX_BACKFILL_RUN: 'fiscal_revenue_tax_backfill_run',
  TAXPAYER_FISCAL_SETUP_GET: 'fiscal_taxpayer_fiscal_setup_get',
  TAXPAYER_FISCAL_PERIOD_SET: 'fiscal_taxpayer_fiscal_period_set',
  TAXPAYER_YEARLY_ELECTION_SET: 'fiscal_taxpayer_yearly_election_set',
  TAX_ASSESSMENT_TAXPAYERS_LIST: 'fiscal_tax_assessment_taxpayers_list',
  TAX_ASSESSMENTS_LIST: 'fiscal_tax_assessments_list',
  TAX_ASSESSMENT_GET: 'fiscal_tax_assessment_get',
  TAX_ASSESSMENT_GENERATE: 'fiscal_tax_assessments_generate',
  TAX_ASSESSMENT_RERUN: 'fiscal_tax_assessment_rerun',
  TAX_ASSESSMENT_APPROVE: 'fiscal_tax_assessment_approve',
  TAX_ASSESSMENT_REGIME_CHANGE_RELEASE_PREVIEW:
    'fiscal_tax_assessment_regime_change_release_preview',
  TAX_ASSESSMENT_REGIME_CHANGE_RELEASE: 'fiscal_tax_assessment_regime_change_release',
  WORKFLOW_CATALOG: 'workflow_catalog',
  WORKFLOW_LIST: 'workflow_list',
  WORKFLOW_GET: 'workflow_get',
  WORKFLOW_CREATE: 'workflow_create',
  WORKFLOW_UPDATE: 'workflow_update',
  WORKFLOW_DELETE: 'workflow_delete',
  WORKFLOW_PREVIEW: 'workflow_preview',
  WORKFLOW_EXECUTIONS_LIST: 'workflow_executions_list',
  MCP_WRITE_CONFIRMATIONS_LIST: 'mcp_write_confirmations_list',
} as const;
export type McpToolName = (typeof McpToolName)[keyof typeof McpToolName];

/** Optional resource-scoped FGA check for a tool. */
export interface McpToolFgaRequirement {
  resourceType: McpFgaResourceType;
  permission: McpFgaPermission;
}

/**
 * Per-tool authorization requirement. Effective access =
 * OAuth scope (consent) ∩ RBAC permission (user ceiling) ∩ FGA (resource).
 */
export interface McpToolAuthRequirement {
  /** OAuth delegation scope the acting user must have consented to. */
  scope: McpOAuthScope;
  /** RBAC permission the acting user must hold — mirrors the platform API. */
  rbacPermission: PermissionKey;
  /** Additional RBAC permissions the platform API requires for this operation. */
  additionalRbacPermissions?: readonly PermissionKey[];
  /** Resource-scoped FGA check; omitted ⇒ tenant-coarse access only (today). */
  fga?: McpToolFgaRequirement;
}

/** Returns every RBAC permission that must be held for an MCP tool. */
export function requiredMcpToolRbacPermissions(requirement: {
  rbacPermission: string;
  additionalRbacPermissions?: readonly string[];
}): readonly string[] {
  return [requirement.rbacPermission, ...(requirement.additionalRbacPermissions ?? [])];
}

const AP_BILLS_READ_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.AP_BILLS_READ,
  rbacPermission: MCP_AP_BILL_RBAC_READ,
  fga: {
    resourceType: McpFgaResourceType.APBILLS,
    permission: McpFgaPermission.APBILLS_READ,
  },
};

const AP_BILLS_MANAGE_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.AP_BILLS_MANAGE,
  rbacPermission: 'ap:bill:update',
};

const AP_BILLS_DOCUMENT_UPLOAD_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.AP_BILLS_MANAGE,
  rbacPermission: 'ap:bill:create',
  fga: {
    resourceType: McpFgaResourceType.APBILLS,
    permission: McpFgaPermission.APBILLS_READ,
  },
};

const AP_PAYMENTS_READ_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.AP_BILLS_READ,
  rbacPermission: MCP_AP_PAYMENT_RBAC_READ,
  fga: {
    resourceType: McpFgaResourceType.APBILLS,
    permission: McpFgaPermission.APBILLS_READ,
  },
};

/** Tenant-coarse read. Payroll confidentiality uses ambient `payrollScope`, not a per-NF FGA resource. */
const AP_RECEIVED_NFS_READ_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.AP_RECEIVED_NFS_READ,
  rbacPermission: MCP_AP_RECEIVED_NF_RBAC_READ,
};

const BUDGET_READ_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.BUDGET_READ,
  rbacPermission: 'budget:read',
};

const BUDGET_LINE_READ_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.BUDGET_READ,
  rbacPermission: 'budget:line:read',
};

const BUDGET_MANAGE_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.BUDGET_MANAGE,
  rbacPermission: 'budget:manage',
};

const BUDGET_LINE_MANAGE_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.BUDGET_MANAGE,
  rbacPermission: 'budget:line:manage',
};

const FPA_READ_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.FPA_READ,
  rbacPermission: 'fpa:read',
};

const FPA_MANAGE_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.FPA_MANAGE,
  rbacPermission: 'fpa:manage',
};

const WORKFLOW_READ_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.WORKFLOW_READ,
  rbacPermission: 'workflow:read',
};

const WORKFLOW_MANAGE_REQUIREMENT: McpToolAuthRequirement = {
  scope: McpOAuthScope.WORKFLOW_MANAGE,
  rbacPermission: 'workflow:manage',
};

/**
 * Single source of truth: each MCP tool → required scope + RBAC permission +
 * (optional) FGA check. Enforcement (`apps/mcp`) is table-driven from this
 * object; discovery `scopes_supported` and WorkOS Connect scope registration
 * are generated from it. The `satisfies Record<McpToolName, …>` clause forces
 * every tool to be mapped (compile-time coverage).
 */
export const MCP_TOOL_AUTH = {
  [McpToolName.AP_BILLS_LIFECYCLE_GUIDE]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.AP_BILLS_UPDATE]: AP_BILLS_MANAGE_REQUIREMENT,
  [McpToolName.AP_BILLS_LIST]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.AP_BILLS_GET]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.AP_BILLS_SUMMARY]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.AP_BILLS_PERIOD_STATS]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.AP_BILLS_TIMELINE]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.AP_BILLS_PAYMENTS_LIST]: AP_PAYMENTS_READ_REQUIREMENT,
  [McpToolName.AP_BILLS_DOCUMENT_UPLOAD_OPEN]: AP_BILLS_DOCUMENT_UPLOAD_REQUIREMENT,
  [McpToolName.AP_BILLS_PAYMENTS_UPDATE]: {
    scope: McpOAuthScope.AP_BILLS_MANAGE,
    rbacPermission: 'ap:payment:update',
  },
  [McpToolName.AP_BILLS_PAYMENTS_CREATE]: {
    scope: McpOAuthScope.AP_BILLS_MANAGE,
    // Parity with POST /ap/bills/:billId/payments.
    rbacPermission: 'ap:payment:create',
  },
  [McpToolName.AP_VENDORS_CREATE]: {
    scope: McpOAuthScope.AP_VENDORS_MANAGE,
    rbacPermission: 'party:manage',
  },
  [McpToolName.AP_RECURRING_BILLS_CREATE]: {
    scope: McpOAuthScope.AP_VENDORS_MANAGE,
    rbacPermission: 'party:manage',
  },
  [McpToolName.AP_VENDORS_FIND]: {
    scope: McpOAuthScope.AP_VENDORS_READ,
    rbacPermission: 'party:read',
  },
  [McpToolName.AP_RECURRING_BILLS_LIST]: {
    scope: McpOAuthScope.AP_VENDORS_READ,
    rbacPermission: 'party:read',
  },
  [McpToolName.AP_RECURRING_BILLS_UPDATE]: {
    scope: McpOAuthScope.AP_VENDORS_MANAGE,
    rbacPermission: 'party:manage',
  },
  [McpToolName.AP_VENDORS_PAYMENT_METHODS_LIST]: {
    scope: McpOAuthScope.AP_BILLS_READ,
    rbacPermission: 'ap:payment:read',
    // Preserve AP access and the API vendor bank-account read boundary.
    additionalRbacPermissions: ['party:read'],
  },
  [McpToolName.AP_VENDORS_PAYMENT_METHODS_CREATE]: {
    scope: McpOAuthScope.AP_VENDORS_MANAGE,
    rbacPermission: 'party:vendor-bank-account:manage',
  },
  [McpToolName.AP_EXPENSE_ACCRUALS_LIST]: {
    scope: McpOAuthScope.EXPENSE_ACCRUALS_READ,
    rbacPermission: 'ap:expense-accrual:read',
  },
  [McpToolName.AP_EXPENSE_ACCRUALS_CONFIRM]: {
    scope: McpOAuthScope.EXPENSE_ACCRUALS_MANAGE,
    rbacPermission: 'ap:expense-accrual:confirm',
  },
  // Parity with /ap/vendor-knowledge: reads follow vendor read access, writes need the manage key.
  // Writes have their own consent scope: `mcp:apvendors:manage` was granted for payment destinations.
  [McpToolName.AP_VENDOR_KNOWLEDGE_LIST]: {
    scope: McpOAuthScope.AP_VENDORS_READ,
    rbacPermission: 'party:read',
  },
  [McpToolName.AP_VENDOR_KNOWLEDGE_RESOLVE]: {
    scope: McpOAuthScope.AP_VENDORS_READ,
    rbacPermission: 'party:read',
  },
  [McpToolName.AP_VENDOR_KNOWLEDGE_CREATE]: {
    scope: McpOAuthScope.AP_VENDOR_KNOWLEDGE_MANAGE,
    rbacPermission: 'ap:vendor-knowledge:manage',
  },
  [McpToolName.AP_VENDOR_KNOWLEDGE_NEW_VERSION]: {
    scope: McpOAuthScope.AP_VENDOR_KNOWLEDGE_MANAGE,
    rbacPermission: 'ap:vendor-knowledge:manage',
  },
  [McpToolName.AP_VENDOR_KNOWLEDGE_DEACTIVATE]: {
    scope: McpOAuthScope.AP_VENDOR_KNOWLEDGE_MANAGE,
    rbacPermission: 'ap:vendor-knowledge:manage',
  },
  [McpToolName.AP_RECEIVED_NFS_LIST]: AP_RECEIVED_NFS_READ_REQUIREMENT,
  [McpToolName.AP_RECEIVED_NFS_GET]: AP_RECEIVED_NFS_READ_REQUIREMENT,
  [McpToolName.GL_CLOSING_CUSTOM_TASKS_LIST]: {
    scope: McpOAuthScope.GL_CLOSING_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.GL_MANUAL_ENTRY_DRAFT_CREATE]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:journal:draft',
  },
  [McpToolName.GL_MANUAL_ENTRY_DRAFT_POST]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:journal:post',
  },
  [McpToolName.ORG_ENTITIES_LIST]: {
    scope: McpOAuthScope.ORG_READ,
    rbacPermission: 'org:read',
  },
  [McpToolName.ORG_COST_CENTERS_LIST]: {
    scope: McpOAuthScope.ORG_READ,
    rbacPermission: 'org:read',
  },
  [McpToolName.ORG_DEPARTMENTS_LIST]: {
    scope: McpOAuthScope.ORG_READ,
    rbacPermission: 'org:read',
  },
  [McpToolName.GL_BOOKS_LIST]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.GL_PERIODS_LIST]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.BUDGET_SETTINGS_GET]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.BUDGET_LIST]: BUDGET_READ_REQUIREMENT,
  [McpToolName.BUDGET_GET]: BUDGET_READ_REQUIREMENT,
  [McpToolName.BUDGET_LINES_LIST]: BUDGET_LINE_READ_REQUIREMENT,
  [McpToolName.BUDGET_LINE_GET]: BUDGET_LINE_READ_REQUIREMENT,
  [McpToolName.BUDGET_RESULTS]: BUDGET_LINE_READ_REQUIREMENT,
  [McpToolName.BUDGET_LINE_UTILIZATION]: BUDGET_LINE_READ_REQUIREMENT,
  [McpToolName.BUDGET_HISTORY]: BUDGET_READ_REQUIREMENT,
  [McpToolName.BUDGET_EXCEPTIONS_LIST]: {
    scope: McpOAuthScope.BUDGET_READ,
    rbacPermission: 'budget:exception:read',
  },
  [McpToolName.BUDGET_CHECK_BILL]: {
    scope: McpOAuthScope.BUDGET_READ,
    rbacPermission: 'ap:bill:read',
  },
  [McpToolName.BUDGET_CREATE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_CREATE_FROM_TEMPLATE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_UPDATE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_DUPLICATE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_CREATE_REVISION]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_VERSIONS_LIST]: BUDGET_READ_REQUIREMENT,
  [McpToolName.BUDGET_LINE_CREATE]: BUDGET_LINE_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_LINE_UPDATE]: BUDGET_LINE_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_LINES_BULK_UPSERT]: BUDGET_LINE_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_LINES_BULK_ARCHIVE]: BUDGET_LINE_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_ACTIVATE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_DEACTIVATE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_ARCHIVE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_REFORECAST_PROMOTION_PREVIEW]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_REFORECAST_PROMOTE]: BUDGET_MANAGE_REQUIREMENT,
  [McpToolName.BUDGET_RESYNC]: BUDGET_MANAGE_REQUIREMENT,
  // These operations are budget-scoped for MCP delegation, while the API's
  // FP&A permissions remain the RBAC ceiling for the underlying rule engine.
  [McpToolName.BUDGET_LINE_DERIVATION_RULE_GET]: {
    scope: McpOAuthScope.BUDGET_READ,
    rbacPermission: 'fpa:read',
  },
  [McpToolName.BUDGET_LINE_DERIVATION_RULE_PUT]: {
    scope: McpOAuthScope.BUDGET_MANAGE,
    rbacPermission: 'fpa:manage',
  },
  [McpToolName.BUDGET_PAYROLL_SCENARIO_SET]: {
    scope: McpOAuthScope.BUDGET_MANAGE,
    rbacPermission: 'fpa:manage',
  },
  [McpToolName.BUDGET_LINE_DERIVED_LIMITS_REVIEW]: {
    scope: McpOAuthScope.BUDGET_READ,
    rbacPermission: 'fpa:read',
    additionalRbacPermissions: ['budget:line:read'],
  },
  [McpToolName.BUDGET_LINE_DERIVED_LIMITS_APPROVE]: BUDGET_LINE_MANAGE_REQUIREMENT,
  [McpToolName.AP_MANAGERIAL_ACCOUNTS_LIST]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.GL_ACCOUNTS_LIST]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.GL_ACCOUNTS_RENAME]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:account:update',
    additionalRbacPermissions: ['gl:read'],
  },
  [McpToolName.GL_ACCOUNTS_CREATE]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:account:create',
    additionalRbacPermissions: ['gl:read'],
  },
  [McpToolName.GL_ACCOUNTS_SET_POSTABLE]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:account:update',
    additionalRbacPermissions: ['gl:read'],
  },
  [McpToolName.GL_ACCOUNTS_SET_STATUS]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:account:update',
    additionalRbacPermissions: ['gl:read'],
  },
  [McpToolName.GL_ACCOUNTS_DELETE]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:manage',
    additionalRbacPermissions: ['gl:read'],
  },
  // Fixed-asset migration (MCP assets_migration_*). The register is GL configuration and GL
  // balances, so it rides the GL scopes. Mapping a class is GL configuration (`gl:manage`, the
  // same key as the class mapping route); importing bens takes `gl:closing:manage`, the key
  // `POST /assets` takes.
  [McpToolName.ASSETS_MIGRATION_READINESS_GET]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.ASSETS_CLASS_ACCOUNTS_SET]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:manage',
    additionalRbacPermissions: ['gl:read'],
  },
  [McpToolName.ASSETS_MIGRATION_IMPORT]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:closing:manage',
    additionalRbacPermissions: ['gl:read'],
  },
  // One bem, the `POST /assets` door: the same `gl:closing:manage` key.
  [McpToolName.ASSETS_REGISTER]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:closing:manage',
    additionalRbacPermissions: ['gl:read'],
  },
  [McpToolName.GL_TRIAL_BALANCE_JSONL_GET]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.GL_JOURNAL_LINES_JSONL_GET]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.GL_LEDGER_VIEW_PRESETS_LIST]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'gl:read',
  },
  [McpToolName.GL_LEDGER_VIEW_PRESETS_CREATE]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:manage',
  },
  [McpToolName.GL_LEDGER_VIEW_PRESETS_UPDATE]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:manage',
  },
  [McpToolName.GL_LEDGER_VIEW_PRESETS_DELETE]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:manage',
  },
  [McpToolName.GL_LEDGER_VIEW_PRESETS_SET_DEFAULT]: {
    scope: McpOAuthScope.GL_MANAGE,
    rbacPermission: 'gl:manage',
  },
  [McpToolName.ORG_SEGMENTS_LIST]: {
    scope: McpOAuthScope.ORG_READ,
    rbacPermission: 'org:read',
  },
  [McpToolName.ORG_SEGMENT_VALUES_LIST]: {
    scope: McpOAuthScope.ORG_READ,
    rbacPermission: 'org:read',
  },
  [McpToolName.IDENTITY_USERS_FIND]: {
    scope: McpOAuthScope.IDENTITY_READ,
    rbacPermission: 'identity:user:read',
  },
  [McpToolName.AR_INVOICES_LIST]: { scope: McpOAuthScope.AR_READ, rbacPermission: 'ar:read' },
  [McpToolName.AR_INVOICES_GET]: { scope: McpOAuthScope.AR_READ, rbacPermission: 'ar:read' },
  [McpToolName.AR_INVOICES_SUMMARY]: { scope: McpOAuthScope.AR_READ, rbacPermission: 'ar:read' },
  [McpToolName.AR_INVOICES_PERIOD_STATS]: {
    scope: McpOAuthScope.AR_READ,
    rbacPermission: 'ar:read',
  },
  [McpToolName.AR_RECEIPTS_LIST]: { scope: McpOAuthScope.AR_READ, rbacPermission: 'ar:read' },
  [McpToolName.AR_CUSTOMERS_FIND]: { scope: McpOAuthScope.AR_READ, rbacPermission: 'party:read' },
  [McpToolName.AR_CUSTOMER_CREATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'party:manage',
  },
  // D46-3 — a foreign settlement customer credit released to revenue (and its reversal): the same
  // permission as the other invoice GL actions (reclassify); the API also requires a master approver.
  [McpToolName.AR_CUSTOMER_CREDIT_RELEASES_GET]: {
    scope: McpOAuthScope.AR_READ,
    rbacPermission: 'ar:read',
  },
  [McpToolName.AR_CUSTOMER_CREDIT_RELEASE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:invoice:reclassify',
  },
  [McpToolName.AR_CUSTOMER_CREDIT_RELEASE_REVERSE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:invoice:reclassify',
  },
  [McpToolName.BILLING_SERVICES_LIST]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:service:read',
  },
  [McpToolName.BILLING_SERVICES_GET]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:service:read',
  },
  [McpToolName.BILLING_SERVICE_CREATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:create',
  },
  [McpToolName.BILLING_SERVICE_UPDATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:edit',
  },
  [McpToolName.BILLING_SERVICE_ARCHIVE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:manage',
  },
  [McpToolName.BILLING_CONTRACTS_LIST]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:contract:read',
  },
  [McpToolName.BILLING_CONTRACTS_GET]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:contract:read',
  },
  [McpToolName.BILLING_CONTRACT_CREATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:contract:manage',
  },
  [McpToolName.BILLING_CONTRACT_ACTIVATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:contract:manage',
  },
  [McpToolName.BILLING_INVOICE_GENERATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:contract:manage',
  },
  [McpToolName.BILLING_AR_FORECAST_GET]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:read',
  },
  [McpToolName.BILLING_SAAS_METRICS_GET]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:contract:read',
  },
  [McpToolName.BILLING_MRR_MOVEMENTS_GET]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:contract:read',
  },
  // ALC-4474 — Cost Services ride the billing scopes and the billing-service RBAC of the API
  // routes (`/billing/cost-services`): reads need ar:service:read, create needs ar:service:create,
  // every other write needs ar:service:edit.
  [McpToolName.COST_SERVICES_CREATE_FROM_TEMPLATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:create',
  },
  [McpToolName.COST_SERVICES_GET]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:service:read',
  },
  [McpToolName.COST_SERVICES_LIST]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:service:read',
  },
  [McpToolName.COST_SERVICES_PATCH_KNOBS]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:edit',
  },
  [McpToolName.COST_SERVICES_ACTIVATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:edit',
  },
  [McpToolName.COST_SERVICES_DEACTIVATE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:edit',
  },
  [McpToolName.COST_SERVICES_ATTACH_BILLING_SERVICE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:edit',
  },
  [McpToolName.COST_SERVICES_END_ATTACHMENT]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:edit',
  },
  [McpToolName.COST_SERVICES_UPDATE_ATTACHMENT]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'ar:service:edit',
  },
  // ALC-4475 — the provisions those Cost Services posted (`/billing/cost-entries`): same RBAC.
  [McpToolName.COST_ENTRIES_LIST_OPEN]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:service:read',
  },
  // S-6 — one provision with its bill matches and reversal/close events: same read RBAC.
  [McpToolName.COST_ENTRIES_GET]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:service:read',
  },
  [McpToolName.COST_ENTRIES_HEAL_PENDING]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'gl:revenue-recognition:manage',
  },
  // ALC-4477 — aging (read) and close-out (write) of those provisions. Heal and close-out post GL
  // journals, so they take the revenue-recognition posting permission (code review, ALC-4473).
  [McpToolName.COST_ENTRIES_AGING]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:service:read',
  },
  [McpToolName.COST_ENTRIES_CLOSE]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'gl:revenue-recognition:manage',
  },
  // D31 (ALC-4473) — closing a bill part still prepaid for an expected provision posts Dr expense /
  // Cr prepaid: the same posting permission as the provision close-out (and the screen's route).
  [McpToolName.COST_ENTRIES_CLOSE_AWAITING]: {
    scope: McpOAuthScope.BILLING_MANAGE,
    rbacPermission: 'gl:revenue-recognition:manage',
  },
  // ALC-4476 — a bill line that pays provisions is an AP bill edit: same scopes as the bill tools.
  [McpToolName.COST_MATCHES_SUGGEST]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.COST_MATCHES_CONFIRM]: AP_BILLS_MANAGE_REQUIREMENT,
  [McpToolName.COST_MATCHES_CREATE]: AP_BILLS_MANAGE_REQUIREMENT,
  [McpToolName.COST_MATCHES_UNDO]: AP_BILLS_MANAGE_REQUIREMENT,
  [McpToolName.COST_LINES_SET_DESTINATION]: AP_BILLS_MANAGE_REQUIREMENT,
  // D27 (ALC-4473) — the line's "Serviço de custo" (all / one / split) is the same bill edit.
  [McpToolName.COST_LINES_SET_SERVICE_CHOICE]: AP_BILLS_MANAGE_REQUIREMENT,
  // ALC-4237 — the balance proof is an accounting assertion about `gl_balance`, so it rides
  // the existing, already-registered GL read scope rather than introducing an `mcp:*` scope
  // the authorization server has not been registered with: `resolveScopeMode` only enforces
  // scopes when the AS supports EVERY entry of this catalog, so an unregistered new scope
  // would silently switch scope enforcement off for every tool. The per-user gate is
  // `op:read`, the same permission the REST route requires, so both adapters authorize alike.
  [McpToolName.RECONCILIATION_BALANCE_PROOF_GET]: {
    scope: McpOAuthScope.GL_READ,
    rbacPermission: 'op:read',
  },
  // The agent's read tools return open AP bills (vendor, amounts, dates, printed charges): the
  // same data `ap_bills_get` serves, so the same scope and permission.
  [McpToolName.RECONCILIATION_OPEN_BILL_GET]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.RECONCILIATION_VENDOR_OPEN_BILLS_LIST]: AP_BILLS_READ_REQUIREMENT,
  [McpToolName.CASHFLOW_PROJECTION_GET]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_SUMMARY_GET]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_VARIANCE_ANALYZE]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_LIQUIDITY_RISKS_LIST]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_RECEIPTS_CONCENTRATION_ANALYZE]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_FUTURE_OBLIGATIONS_LIST]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_ALERT_EXPLAIN]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_CELL_DRILLDOWN]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    rbacPermission: 'report:read',
  },
  [McpToolName.CASHFLOW_MANUAL_ITEMS_LIST]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    // Parity with API GET /cashflow/manual-items (@RequirePermission).
    rbacPermission: 'report:cash-flow:manage',
  },
  [McpToolName.CASHFLOW_LIQUIDITY_CONFIG_GET]: {
    scope: McpOAuthScope.CASHFLOW_READ,
    // Parity with API GET /cashflow/liquidity-config (@RequirePermission).
    rbacPermission: 'settings:read',
  },
  [McpToolName.CASHFLOW_MANUAL_ITEM_CREATE]: {
    scope: McpOAuthScope.CASHFLOW_MANAGE,
    rbacPermission: 'report:cash-flow:manage',
  },
  [McpToolName.CASHFLOW_MANUAL_ITEM_UPDATE]: {
    scope: McpOAuthScope.CASHFLOW_MANAGE,
    rbacPermission: 'report:cash-flow:manage',
  },
  [McpToolName.CASHFLOW_MANUAL_ITEM_DELETE]: {
    scope: McpOAuthScope.CASHFLOW_MANAGE,
    rbacPermission: 'report:cash-flow:manage',
  },
  [McpToolName.CASHFLOW_LIQUIDITY_CONFIG_PUT]: {
    scope: McpOAuthScope.CASHFLOW_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.FPA_TOOLS_GUIDE]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_PLAYBOOKS_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_PLAYBOOKS_GET]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_ROLLUPS_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_ROLLUPS_GET]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_ROLLUPS_CREATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_ROLLUPS_UPDATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_ROLLUPS_ARCHIVE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_ROLLUP_RESTORE_DEFAULT]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_FORECAST_RULE_SETS_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_FORECAST_RULE_SETS_GET]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_FORECAST_RULE_SETS_CREATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_FORECAST_RULE_SETS_UPDATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_FORECAST_RULE_SETS_ARCHIVE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_TEMPLATES_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_TEMPLATES_GET]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_TEMPLATES_CREATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_TEMPLATES_CREATE_FROM_STANDARD]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_TEMPLATES_UPDATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_TEMPLATES_ARCHIVE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_TEMPLATES_UNARCHIVE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_TEMPLATE_EVALUATE]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_BUDGET_VS_ACTUAL]: {
    ...FPA_READ_REQUIREMENT,
    // The response includes budget-line IDs and utilization amounts, so both
    // source permissions are required even though its OAuth consent is FP&A.
    additionalRbacPermissions: ['budget:line:read'],
  },
  [McpToolName.FPA_EXECUTIVE_SNAPSHOT]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_VARIANCE_EXPLAIN]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_CELL_DRILLDOWN]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_METRICS_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_METRIC_SERIES_UPSERT]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_REVENUE_WATERFALL]: {
    scope: McpOAuthScope.BILLING_READ,
    rbacPermission: 'ar:contract:read',
  },
  [McpToolName.FPA_METRIC_RESTATEMENT_REQUEST]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_METRIC_RESTATEMENT_APPROVE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_METRIC_SERIES_DELETE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_METRIC_POINT_DELETE]: FPA_MANAGE_REQUIREMENT,
  // Parity with GET /fpa/metrics/:seriesId/points/:period/sources: the payload
  // exposes billing contract evidence, so the billing contract read ceiling applies.
  [McpToolName.FPA_METRIC_POINT_SOURCES_GET]: {
    scope: McpOAuthScope.FPA_READ,
    rbacPermission: 'ar:contract:read',
  },
  [McpToolName.FPA_ASSUMPTIONS_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_ASSUMPTIONS_CREATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_ASSUMPTIONS_UPDATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_ASSUMPTIONS_DELETE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_LINE_OVERRIDES_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_LINE_OVERRIDES_CREATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_LINE_OVERRIDES_DELETE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_SNAPSHOT_FREEZE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_SNAPSHOTS_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_SNAPSHOTS_GET]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_SNAPSHOTS_DIFF]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_SNAPSHOT_ASOF_GET]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_SNAPSHOT_CELL_DRILLDOWN]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_FORECAST_ACCURACY]: FPA_READ_REQUIREMENT,
  [McpToolName.HCM_PJ_COLLABORATOR_CREATE]: {
    scope: McpOAuthScope.HCM_MANAGE,
    rbacPermission: 'hcm:manage',
    additionalRbacPermissions: ['party:manage', 'fga:manage'],
  },
  [McpToolName.FPA_HEADCOUNT_PLAN_CREATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_HEADCOUNT_PLAN_GET]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_HEADCOUNT_PLAN_REPORT]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_HEADCOUNT_HIRE_SUGGESTIONS_LIST]: FPA_READ_REQUIREMENT,
  [McpToolName.FPA_HEADCOUNT_POSITION_CREATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_HEADCOUNT_POSITION_UPDATE]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_HEADCOUNT_POSITION_CANCEL]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.FPA_HEADCOUNT_HIRE_CONFIRM]: FPA_MANAGE_REQUIREMENT,
  [McpToolName.INTAKE_RULE_LIST]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.INTAKE_RULE_GET]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.INTAKE_RULE_REPLAY]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  // Parity with API POST /ap/intake-rules/propose (@RequirePermission('settings:manage')) — the
  // tool never persists, but calling it without settings:manage would clear MCP's own check and
  // then get an opaque 403 from the API (mirrors CASHFLOW_MANUAL_ITEMS_LIST above).
  [McpToolName.INTAKE_RULE_PROPOSE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.INTAKE_RULE_CREATE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.INTAKE_RULE_UPDATE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.INTAKE_RULE_DELETE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  // Revenue Tax setup (ALC-4410): parity with the API, where GET fiscal/revenue-tax/setup is
  // `settings:read` and every write is `settings:manage` (@TenantScoped).
  [McpToolName.REVENUE_TAX_SETUP_GET]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.REVENUE_TAX_CLASSES_SET]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.REVENUE_TAX_ACCOUNTS_ENSURE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.REVENUE_TAX_CONFIGURE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.REVENUE_TAX_RULE_OVERRIDE_UPSERT]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.REVENUE_TAX_RULE_OVERRIDE_END]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  // Revenue Tax backfill (ALC-4415): parity with POST fiscal/revenue-tax/backfill/preview and /run,
  // both `settings:manage`. The preview never persists, but calling it without settings:manage would
  // clear MCP's own check and then get an opaque 403 from the API (like INTAKE_RULE_PROPOSE).
  [McpToolName.REVENUE_TAX_BACKFILL_PREVIEW]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.REVENUE_TAX_BACKFILL_RUN]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.TAXPAYER_FISCAL_SETUP_GET]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.TAXPAYER_FISCAL_PERIOD_SET]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.TAXPAYER_YEARLY_ELECTION_SET]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.TAX_ASSESSMENT_TAXPAYERS_LIST]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.TAX_ASSESSMENTS_LIST]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.TAX_ASSESSMENT_GET]: {
    scope: McpOAuthScope.SETTINGS_READ,
    rbacPermission: 'settings:read',
  },
  [McpToolName.TAX_ASSESSMENT_GENERATE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.TAX_ASSESSMENT_RERUN]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.TAX_ASSESSMENT_APPROVE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  // The screen route requires settings:manage; the tool runs the same core in process (ALC-4688).
  [McpToolName.TAX_ASSESSMENT_REGIME_CHANGE_RELEASE_PREVIEW]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.TAX_ASSESSMENT_REGIME_CHANGE_RELEASE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [McpToolName.WORKFLOW_CATALOG]: WORKFLOW_READ_REQUIREMENT,
  [McpToolName.WORKFLOW_LIST]: WORKFLOW_READ_REQUIREMENT,
  [McpToolName.WORKFLOW_GET]: WORKFLOW_READ_REQUIREMENT,
  [McpToolName.WORKFLOW_CREATE]: WORKFLOW_MANAGE_REQUIREMENT,
  [McpToolName.WORKFLOW_UPDATE]: WORKFLOW_MANAGE_REQUIREMENT,
  [McpToolName.WORKFLOW_DELETE]: WORKFLOW_MANAGE_REQUIREMENT,
  [McpToolName.WORKFLOW_PREVIEW]: WORKFLOW_READ_REQUIREMENT,
  [McpToolName.WORKFLOW_EXECUTIONS_LIST]: WORKFLOW_READ_REQUIREMENT,
  // The agent's own approval inbox (ALC-3889). It discloses NOTHING beyond the
  // confirmations the acting user themself requested, so it carries the lowest
  // privilege the registry can express: `dashboard:read`, which every
  // authenticated user in a tenant holds. The real isolation is in the service
  // (tenantId + requestedByUserId), not in this entry — see
  // `listWriteConfirmations` and its integration spec.
  [McpToolName.MCP_WRITE_CONFIRMATIONS_LIST]: {
    scope: McpOAuthScope.IDENTITY_READ,
    rbacPermission: 'dashboard:read',
  },
} as const satisfies Record<McpToolName, McpToolAuthRequirement>;

/** API writes that use the confirmation lifecycle before their MCP tool is registered. */
export const ConfirmedApiWriteName = {
  TAX_ASSESSMENT_GENERATE: McpToolName.TAX_ASSESSMENT_GENERATE,
  TAX_ASSESSMENT_RERUN: McpToolName.TAX_ASSESSMENT_RERUN,
  TAX_ASSESSMENT_APPROVE: McpToolName.TAX_ASSESSMENT_APPROVE,
  TAX_ASSESSMENT_REGIME_CHANGE_RELEASE: McpToolName.TAX_ASSESSMENT_REGIME_CHANGE_RELEASE,
  TAX_ENTITLEMENT_CORRECTION: 'tax_entitlement_correction',
  TAX_ENTITLEMENT_SEGREGATION: 'tax_entitlement_segregation',
} as const;

const CONFIRMED_API_WRITE_AUTH = {
  [ConfirmedApiWriteName.TAX_ASSESSMENT_GENERATE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [ConfirmedApiWriteName.TAX_ASSESSMENT_RERUN]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [ConfirmedApiWriteName.TAX_ASSESSMENT_APPROVE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [ConfirmedApiWriteName.TAX_ASSESSMENT_REGIME_CHANGE_RELEASE]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [ConfirmedApiWriteName.TAX_ENTITLEMENT_CORRECTION]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
  [ConfirmedApiWriteName.TAX_ENTITLEMENT_SEGREGATION]: {
    scope: McpOAuthScope.SETTINGS_MANAGE,
    rbacPermission: 'settings:manage',
  },
} as const satisfies Record<string, McpToolAuthRequirement>;

/** Resolve the authorization requirement for a registered tool or confirmed API write. */
export function getMcpToolAuthRequirement(toolName: string): McpToolAuthRequirement | undefined {
  return (
    (MCP_TOOL_AUTH as Record<string, McpToolAuthRequirement>)[toolName] ??
    (CONFIRMED_API_WRITE_AUTH as Record<string, McpToolAuthRequirement>)[toolName]
  );
}

/** True when the consented scope set includes the required delegation scope. */
export function tokenIncludesScope(scopes: readonly string[], required: McpOAuthScope): boolean {
  return scopes.includes(required);
}

/**
 * All distinct MCP OAuth scopes referenced by the tool registry — the source
 * for the protected-resource metadata `scopes_supported` and WorkOS Connect
 * scope registration (so neither is hand-maintained).
 */
export const MCP_OAUTH_SCOPES_SUPPORTED: readonly McpOAuthScope[] = Object.freeze(
  Array.from(new Set(Object.values(MCP_TOOL_AUTH).map((requirement) => requirement.scope))),
);
